import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FaMapMarkerAlt } from 'react-icons/fa';

// Create blue marker icon using SVG
const blueIcon = L.divIcon({
    className: 'custom-blue-marker',
    html: `
        <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 8.75 12.5 28.5 12.5 28.5S25 21.25 25 12.5C25 5.596 19.404 0 12.5 0z" fill="#2563eb" stroke="#ffffff" stroke-width="1"/>
            <circle cx="12.5" cy="12.5" r="5" fill="#ffffff"/>
        </svg>
    `,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
});

const LeafletMap = ({ 
    location, 
    height = '400px', 
    zoom = 13,
    showOpenInMaps = true,
    className = '',
    style = {}
}) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);

    useEffect(() => {
        if (!location || !mapRef.current) return;

        const defaultLat = 24.8607; // Default to Pakistan center
        const defaultLng = 67.0011;

        // Initialize map if it doesn't exist
        if (!mapInstanceRef.current) {
            mapInstanceRef.current = L.map(mapRef.current, {
                center: [defaultLat, defaultLng],
                zoom: zoom,
                zoomControl: false, // Disable default zoom control
                attributionControl: true
            });

            // Add OpenStreetMap tile layer
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 19
            }).addTo(mapInstanceRef.current);

            // Add zoom control at bottom-right with lower z-index
            const zoomControl = L.control.zoom({
                position: 'bottomright'
            }).addTo(mapInstanceRef.current);

            // Set z-index for Leaflet controls to be below header (header is 1000)
            setTimeout(() => {
                if (mapRef.current) {
                    const leafletControls = mapRef.current.querySelectorAll('.leaflet-control-container');
                    leafletControls.forEach(container => {
                        container.style.zIndex = '999';
                    });
                }
            }, 100);
        }

        // Geocode function - can be called when map exists or location changes
        const geocodeAddress = async () => {
            // Check if map still exists before proceeding
            if (!mapInstanceRef.current || !mapRef.current) {
                return;
            }

            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&limit=1`,
                    {
                        headers: {
                            'User-Agent': 'Maveshibids/1.0'
                        }
                    }
                );
                const data = await response.json();
                
                // Check again if map still exists after async operation
                if (!mapInstanceRef.current || !mapRef.current) {
                    return;
                }
                
                if (data && data.length > 0) {
                    const lat = parseFloat(data[0].lat);
                    const lng = parseFloat(data[0].lon);
                    
                    mapInstanceRef.current.setView([lat, lng], zoom);
                    
                    if (markerRef.current && mapInstanceRef.current.hasLayer(markerRef.current)) {
                        mapInstanceRef.current.removeLayer(markerRef.current);
                    }
                    markerRef.current = L.marker([lat, lng], { icon: blueIcon })
                        .addTo(mapInstanceRef.current)
                        .bindPopup(`<b>Estimated Location</b><br/><small>${location}</small>`)
                        .openPopup();
                } else {
                    // Check again before adding default marker
                    if (!mapInstanceRef.current || !mapRef.current) {
                        return;
                    }
                    if (markerRef.current && mapInstanceRef.current.hasLayer(markerRef.current)) {
                        mapInstanceRef.current.removeLayer(markerRef.current);
                    }
                    markerRef.current = L.marker([defaultLat, defaultLng], { icon: blueIcon })
                        .addTo(mapInstanceRef.current)
                        .bindPopup(`<b>Estimated Location</b><br/><small>${location}</small>`)
                        .openPopup();
                }
            } catch (error) {
                console.error('Geocoding error:', error);
                // Check again before adding error marker
                if (!mapInstanceRef.current || !mapRef.current) {
                    return;
                }
                if (markerRef.current && mapInstanceRef.current.hasLayer(markerRef.current)) {
                    mapInstanceRef.current.removeLayer(markerRef.current);
                }
                markerRef.current = L.marker([defaultLat, defaultLng], { icon: blueIcon })
                    .addTo(mapInstanceRef.current)
                    .bindPopup(`<b>Estimated Location</b><br/><small>${location}</small>`)
                    .openPopup();
            }
        };

        // Wait a bit for map to fully initialize before geocoding (or if location changed)
        setTimeout(() => {
            if (mapInstanceRef.current && mapRef.current) {
                geocodeAddress();
            }
        }, 100);

        return () => {
            if (mapInstanceRef.current) {
                try {
                    // Remove marker first
                    if (markerRef.current && mapInstanceRef.current.hasLayer(markerRef.current)) {
                        mapInstanceRef.current.removeLayer(markerRef.current);
                    }
                    // Then remove the map
                    mapInstanceRef.current.remove();
                } catch (error) {
                    console.error('Error cleaning up map:', error);
                } finally {
                    mapInstanceRef.current = null;
                    markerRef.current = null;
                }
            }
        };
    }, [location, zoom]);

    if (!location) {
        return (
            <div style={{
                width: '100%',
                height: height,
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b'
            }}>
                <p className="mb-0" style={{ fontSize: '14px' }}>
                    Location not available
                </p>
            </div>
        );
    }

    return (
        <div className={className} style={style}>
            <style>{`
                .leaflet-control-container {
                    z-index: 999 !important;
                }
                .leaflet-top,
                .leaflet-bottom {
                    z-index: 999 !important;
                }
            `}</style>
            <div
                ref={mapRef}
                style={{
                    width: '100%',
                    height: height,
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    position: 'relative',
                    zIndex: 1
                }}
            />
            {showOpenInMaps && (
                <div className="mt-3">
                    <a
                        href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                        style={{
                            fontSize: '14px',
                            color: '#2563eb',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#1d4ed8';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#2563eb';
                        }}
                    >
                        <FaMapMarkerAlt style={{ fontSize: '12px' }} />
                        Open in OpenStreetMap
                    </a>
                </div>
            )}
        </div>
    );
};

export default LeafletMap;

